import { ComponentProps, RefObject } from 'preact';
import { useCallback, useState } from 'preact/hooks';

import { DataState, ListItemContext } from '#UNSAFE_Collection';
import { Menu } from '#UNSAFE_Menu';
import { findItemContext } from '#hooks/PRIVATE_useItemAction';
import { useContextMenuGesture } from '#hooks/UNSAFE_useContextMenuGesture';
import { ITEM_SELECTOR, findElementByKey, keyExtractor } from '#utils/PRIVATE_collectionUtils';
import { ListView } from './ListView';

type MenuProps = Pick<
  ComponentProps<typeof Menu>,
  'anchorRef' | 'initialFocus' | 'placement' | 'offsetValue' | 'isOpen'
>;

const TOUCH_OFFSET_VALUE = 40;

const menuPropGestureStates: Record<
  'mouse' | 'keyboard' | 'touch',
  Omit<Required<MenuProps>, 'anchorRef' | 'isOpen'>
> = {
  mouse: {
    initialFocus: 'menu',
    placement: 'bottom-start',
    offsetValue: 0
  },
  keyboard: {
    initialFocus: 'firstItem',
    placement: 'bottom-start',
    offsetValue: 0
  },
  touch: {
    initialFocus: 'menu',
    placement: 'end',
    offsetValue: TOUCH_OFFSET_VALUE
  }
};

const initialMenuProps: MenuProps = {
  isOpen: false,
  initialFocus: 'menu',
  placement: 'bottom-start',
  offsetValue: 0,
  anchorRef: { current: null }
};

type ListViewProps<K extends string | number, D> = ComponentProps<typeof ListView<K, D>>;

export const useListViewContextMenu = <K extends string | number, D>(
  dataState: DataState<K, D> | null,
  contextMenuConfig: ListViewProps<K, D>['contextMenuConfig'],
  currentKey: ListViewProps<K, D>['currentKey'],
  onCurrentKeyChange: ListViewProps<K, D>['onCurrentKeyChange'],
  scrollListRef: RefObject<HTMLElement | null>
) => {
  /*Context Menu section
   TODO: A version of context Menu listview will be merged using useContextMenuGesture instead. Once it's merged we 
   can go back to this to create a hook or utility that could be share among all collection comps. JET-60320
  */

  /* 
  Steps to open a menu:
    1.- useContextMenu handler is called when menu is supposed to be opened.
    2.- We set contextMenuContext when contextMenuRenderer is called.
    3.- We dispatch opening the menu with the correct anchor.
    5.- Menu receives updated props and updated contextMenuContext.
    6.- Menu is opened.  

    */

  //Supposed to be updated when menu is about to be opened
  //We set an initial key value for the context menu context, to allow user use object destructuring inside the
  //contextMenuRenderer. It doesn't matter if the key is not correct at the initial rendering since menu starts
  //being closed always
  const [contextMenuContext, setContextMenuContext] = useState<ListItemContext<K, D>>();

  const [menuProps, setMenuProps] = useState<MenuProps>(initialMenuProps);

  const { triggerProps } = useContextMenuGesture(
    ({ gesture, anchor, target }) => {
      let anchorBasedOnGesture = anchor;
      if (gesture === 'keyboard') {
        //We find the element where the position of the menu is going to be based of
        const elem = findElementByKey(
          scrollListRef.current as HTMLElement,
          currentKey as string,
          ITEM_SELECTOR
        );

        //We set the correct anchor
        anchorBasedOnGesture = elem ? elem : anchor;

        const context = findItemContext(
          currentKey,
          dataState as DataState<string | number | null, D>
        );

        setContextMenuContext(context as ListItemContext<K, D>);
      } else {
        //We search for the key using the target of the event
        const key = keyExtractor(target as HTMLElement, ITEM_SELECTOR);

        const context = findItemContext(key, dataState as DataState<string | number | null, D>);

        setContextMenuContext(context as ListItemContext<K, D>);

        //We update the key so when we close logical focus is on the correct item
        //TODO: Do this directly in useCurrentKey.JET-62373
        onCurrentKeyChange?.({ value: key as K });
      }
      setMenuProps({
        ...menuPropGestureStates[gesture],
        anchorRef: { current: anchorBasedOnGesture },
        isOpen: true
      });
    },
    {
      isDisabled: !contextMenuConfig //If there is no a context menu renderer we disable the hook
    }
  );

  const handleCloseContextMenu = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      /* 
      TODO: Focus ring should be visible when context menu was closed because of selecting an item using keyboard, 
      but should not be visible when the selection was done using mouse or touch. This would probably means menu 
      onClose API is going to be changed a little bit so we can satisfy this requirement. JET-62372
      */
      //Focus has to be set on the current target that was obtained
      //during context menu gesture when menu is dismissed or item is "selected"
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        scrollListRef.current?.focus({ preventScroll: true });
      }
      // We close the menu.The only prop that matters here is isOpen
      setMenuProps({ ...initialMenuProps });
    },
    [scrollListRef]
  );

  return {
    menuProps: { ...menuProps, onClose: handleCloseContextMenu },
    onClose: handleCloseContextMenu,
    contextMenuContext: contextMenuContext as ListItemContext<K, D>,
    triggerProps
  } as const;
};
